import { useEffect, useId, useRef, type ReactNode } from "react";

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Buttons rendered in the footer. */
  actions?: ReactNode;
}

/**
 * Modal built on the native <dialog>: focus trapping, Escape to close and the backdrop
 * come from the browser, so screen readers get correct semantics for free.
 */
export function Dialog({ open, title, onClose, children, actions }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current!;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="hx-dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {open && (
        <>
          <div className="hx-dialog__header">
            <h2 id={titleId}>{title}</h2>
            <button
              type="button"
              className="hx-button hx-button--ghost hx-button--small"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          {children}
          {actions && <div className="hx-dialog__actions">{actions}</div>}
        </>
      )}
    </dialog>
  );
}
